import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  PersonAdd,
  People,
  ShoppingCart,
  ExitToApp,
  Settings,
  SupervisorAccount,
  CloudSync,
  Notifications,
  Email,
  ExpandLess,
  ExpandMore,
  NotificationImportant,
  Tune,
  Campaign,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBackofficeAuth } from '../hooks/useBackofficeAuth';
import { ROUTES } from '../config/constants';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Footer from '../components/Footer';

const drawerWidth = 240;

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path?: string;
  requiredRole?: string;
  children?: NavigationItem[];
}

const getNavigationItems = (t: any): NavigationItem[] => {
  return [
    {
      text: t('navigation.dashboard'),
      icon: <Dashboard />,
      path: ROUTES.BACKOFFICE_DASHBOARD,
    },
    {
      text: t('backoffice.signupRequests.title'),
      icon: <PersonAdd />,
      path: ROUTES.BACKOFFICE_SIGNUP_REQUESTS,
    },
    {
      text: t('navigation.customers'),
      icon: <People />,
      path: ROUTES.BACKOFFICE_CUSTOMER_MANAGEMENT,
    },
    {
      text: t('navigation.orders'),
      icon: <ShoppingCart />,
      path: ROUTES.BACKOFFICE_ORDER_MANAGEMENT,
    },
    {
      text: 'Product Info',
      icon: <Settings />,
      path: ROUTES.BACKOFFICE_PRODUCT_INFO,
    },
    {
      text: 'Promotions',
      icon: <Campaign />,
      path: ROUTES.BACKOFFICE_PROMOTIONS,
    },
    {
      text: 'Epicor Connect',
      icon: <CloudSync />,
      path: ROUTES.BACKOFFICE_EPICOR_CONNECT,
      requiredRole: 'admin',
    },
    {
      text: 'User Management',
      icon: <SupervisorAccount />,
      path: ROUTES.BACKOFFICE_USER_MANAGEMENT,
      requiredRole: 'super_admin',
    },
    {
      text: 'Notifications',
      icon: <Notifications />,
      requiredRole: 'admin',
      children: [
        {
          text: 'Notification Templates',
          icon: <Email />,
          path: ROUTES.BACKOFFICE_NOTIFICATION_TEMPLATES,
          requiredRole: 'admin',
        },
        {
          text: 'Notification Settings',
          icon: <Tune />,
          path: ROUTES.BACKOFFICE_NOTIFICATION_SETTINGS,
          requiredRole: 'admin',
        },
        {
          text: 'Notification Management',
          icon: <NotificationImportant />,
          path: ROUTES.BACKOFFICE_NOTIFICATION_MANAGEMENT,
          requiredRole: 'admin',
        },
      ],
    },
    {
      text: 'System Config',
      icon: <Settings />,
      path: '/backoffice/system-config',
      requiredRole: 'super_admin',
    },
  ];
};

const BackofficeLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    'Notifications': false,
  });
  const { t } = useTranslation();
  const { user, logout } = useBackofficeAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate(ROUTES.BACKOFFICE_LOGIN);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleSectionToggle = (sectionText: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionText]: !prev[sectionText],
    }));
  };

  const filterNavigationItems = (items: NavigationItem[]): NavigationItem[] => {
    return items.filter(item => {
      // If no role required, show to all users
      if (!item.requiredRole) return true;
      // If role required, check if user has that role
      const hasPermission = user?.role === item.requiredRole || user?.role === 'super_admin';
      
      if (item.children) {
        // For parent items with children, also filter children
        item.children = filterNavigationItems(item.children);
        // Show parent if it has permission and has visible children
        return hasPermission && item.children.length > 0;
      }
      
      return hasPermission;
    });
  };

  const navigationItems = filterNavigationItems(getNavigationItems(t));
  
  // Check if current path is in notification section to auto-expand
  React.useEffect(() => {
    const notificationPaths: string[] = [
      ROUTES.BACKOFFICE_NOTIFICATION_TEMPLATES,
      ROUTES.BACKOFFICE_NOTIFICATION_SETTINGS,
      ROUTES.BACKOFFICE_NOTIFICATION_MANAGEMENT,
    ];
    
    if (notificationPaths.includes(location.pathname)) {
      setExpandedSections(prev => ({
        ...prev,
        'Notifications': true,
      }));
    }
  }, [location.pathname]);

  const renderNavigationItem = (item: NavigationItem) => {
    if (item.children && item.children.length > 0) {
      // Render parent item with children (collapsible section)
      const isExpanded = expandedSections[item.text];
          
      return (
        <React.Fragment key={item.text}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleSectionToggle(item.text)}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((childItem) => {
                const isActive = location.pathname === childItem.path;
                
                return (
                  <ListItem key={childItem.text} disablePadding>
                    <ListItemButton
                      selected={isActive}
                      onClick={() => childItem.path ? handleNavigation(childItem.path) : undefined}
                      sx={{
                        pl: 4,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: isActive ? 'primary.main' : 'inherit',
                          minWidth: 36,
                        }}
                      >
                        {childItem.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={childItem.text}
                        sx={{
                          color: isActive ? 'primary.main' : 'inherit',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    } else {
      // Render regular navigation item
      const isActive = location.pathname === item.path;
      
      return (
        <ListItem key={item.text} disablePadding>
          <ListItemButton
            selected={isActive}
            onClick={() => item.path ? handleNavigation(item.path) : undefined}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive ? 'primary.main' : 'inherit',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              sx={{
                color: isActive ? 'primary.main' : 'inherit',
              }}
            />
          </ListItemButton>
        </ListItem>
      );
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <img 
          src="/Mowilex-Premium-Paints.webp" 
          alt="Mowilex Premium Paints" 
          style={{ height: 40, maxWidth: 200, objectFit: 'contain' }}
        />
      </Toolbar>
      <Divider />
      <List>
        {navigationItems.map((item) => renderNavigationItem(item))}
      </List>
    </div>
  );

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'user':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Customer Portal - Backoffice
          </Typography>

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LanguageSwitcher />
              <Chip 
                label={user.role.toUpperCase()} 
                color={getRoleColor(user.role)}
                size="small"
                variant="outlined"
                sx={{ color: 'white', borderColor: 'white' }}
              />
              <IconButton
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32 }}>
                  {user.full_name.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Box>
          )}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {user?.full_name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {user?.email}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              {t('navigation.settings')}
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              {t('navigation.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, p: 3 }}>
          <Outlet />
        </Box>
        <Footer variant="backoffice" />
      </Box>
    </Box>
  );
};

export default BackofficeLayout;